import React, { useEffect, useRef, useState, useCallback } from 'react';
import { openRemoteChannel } from './remoteTransport';
import {
  MSG, CMD, HEARTBEAT_MS, STALE_AFTER_MS,
  normalizeCode, isValidCode, formatCode,
} from './remoteProtocol';

const codeFromUrl = () => normalizeCode(new URLSearchParams(window.location.search).get('c') || '');

/**
 * The phone remote (/remote): pair with a code, then move through the
 * run-sheet and send slides live. Needs no login; the pairing code is the key.
 * All text is rendered as React text, never innerHTML.
 */
const RemoteScreen = () => {
  const [code, setCode] = useState(() => (isValidCode(codeFromUrl()) ? codeFromUrl() : ''));
  const [typed, setTyped] = useState('');
  const [link, setLink] = useState('connecting'); // our link to Supabase
  const [state, setState] = useState(null);       // last snapshot from the console
  const [consoleSeen, setConsoleSeen] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const remoteRef = useRef(null);

  useEffect(() => { document.title = 'SleekHear Remote'; }, []);

  useEffect(() => {
    if (!code) return undefined;
    setState(null);
    setConsoleSeen(0);
    const remote = openRemoteChannel(code, {
      onStatus: (s) => {
        setLink(s);
        if (s === 'connected' && remoteRef.current) remoteRef.current.send({ t: MSG.HELLO });
      },
      onMessage: (msg) => {
        if (msg.t === MSG.STATE && msg.state) {
          setState(msg.state);
          setConsoleSeen(Date.now());
        }
      },
    });
    remoteRef.current = remote;
    if (!remote) { setLink('offline'); return undefined; }

    const timer = setInterval(() => {
      remote.send({ t: MSG.PING });
      setNow(Date.now());
    }, HEARTBEAT_MS);
    return () => {
      clearInterval(timer);
      remote.close();
      remoteRef.current = null;
    };
  }, [code]);

  const send = useCallback((cmd, extra) => {
    if (remoteRef.current) remoteRef.current.send({ t: MSG.CMD, cmd, ...extra });
    if (navigator.vibrate) navigator.vibrate(15);
  }, []);

  const pair = (e) => {
    e.preventDefault();
    if (isValidCode(typed)) setCode(normalizeCode(typed));
  };

  const shell = 'min-h-dvh bg-slate-950 text-slate-50 p-4 flex flex-col';

  if (!code) {
    return (
      <div className={`${shell} items-center justify-center`}>
        <form onSubmit={pair} className="w-full max-w-xs text-center">
          <h1 className="text-2xl font-bold mb-2">SleekHear Remote</h1>
          <p className="text-slate-400 text-sm mb-4">Enter the code shown on the console.</p>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="XXXX-XXXX"
            aria-label="Pairing code"
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect="off"
            className="w-full text-center text-3xl font-mono tracking-widest p-3 rounded-lg bg-slate-900 border border-slate-700 mb-3"
          />
          <button
            type="submit"
            disabled={!isValidCode(typed)}
            className="w-full py-3 rounded-lg bg-blue-600 font-bold disabled:opacity-40"
          >
            Connect
          </button>
        </form>
      </div>
    );
  }

  const consoleOnline = state && now - consoleSeen < STALE_AFTER_MS;
  const statusText = link !== 'connected' ? (link === 'connecting' ? 'Connecting…' : 'Reconnecting…')
    : consoleOnline ? 'Connected' : 'Waiting for the console…';
  const dot = statusText === 'Connected' ? 'bg-green-400' : 'bg-yellow-400';
  const ready = link === 'connected' && consoleOnline;

  const big = 'rounded-xl font-bold text-lg disabled:opacity-40 active:scale-95 transition-transform';
  const items = (state && state.items) || [];
  const live = state && state.live && state.live.label;
  const line = state && state.line;

  return (
    <div className={shell}>
      <header className="flex items-center justify-between mb-3">
        <div className="min-w-0">
          <h1 className="text-lg font-bold truncate">{(state && state.planName) || 'SleekHear Remote'}</h1>
          <p className="text-xs text-slate-400">{formatCode(code)}</p>
        </div>
        <div className="flex items-center gap-2 text-sm" role="status">
          <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
          {statusText}
        </div>
      </header>

      <section className="rounded-xl border border-slate-700 bg-slate-900 p-3 mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-400">Staged</div>
        <div className="text-xl font-bold truncate">{(state && state.staged && state.staged.label) || '—'}</div>
      </section>
      <section className={`rounded-xl border p-3 mb-3 ${state && state.isLive && live ? 'border-red-500 bg-red-950/40' : 'border-slate-700 bg-slate-900'}`}>
        <div className="text-xs uppercase tracking-wider text-slate-400">
          {state && state.isLive ? 'On air' : 'Off air'}
        </div>
        <div className="text-xl font-bold truncate">{live || '—'}</div>
        {line && <div className="text-sm text-slate-300 mt-1 break-words">{line.text}</div>}
      </section>

      {line && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <button className={`${big} py-4 bg-slate-700`} disabled={!ready || line.index <= 0} onClick={() => send(CMD.LINE_PREV)}>◀ Line</button>
          <button className={`${big} py-4 bg-slate-700`} disabled={!ready || line.index >= line.total - 1} onClick={() => send(CMD.LINE_NEXT)}>Line ▶</button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-3">
        <button className={`${big} py-5 bg-slate-700`} disabled={!ready} onClick={() => send(CMD.PREV)}>◀ Prev</button>
        <button className={`${big} py-5 bg-blue-700`} disabled={!ready} onClick={() => send(CMD.NEXT)}>Next ▶</button>
        <button className={`${big} py-6 bg-red-600 text-xl`} disabled={!ready} onClick={() => send(CMD.SEND)}>Go live</button>
        <button className={`${big} py-6 bg-slate-600 text-xl`} disabled={!ready} onClick={() => send(CMD.CLEAR)}>Clear</button>
      </div>

      <section className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-slate-800">
        {items.length === 0 ? (
          <p className="p-3 text-sm text-slate-500">The run-sheet is empty.</p>
        ) : (
          <ul>
            {items.map((item, i) => (
              <li key={i} className={`flex items-stretch border-b border-slate-800 ${i === state.currentIndex ? 'bg-blue-900/50' : ''}`}>
                <button
                  disabled={!ready}
                  onClick={() => send(CMD.GOTO, { index: i })}
                  aria-label={`Stage ${item.label}`}
                  className={`flex-1 min-w-0 text-left px-3 py-3 flex items-center gap-2 disabled:opacity-60 ${i === state.currentIndex ? 'font-bold' : ''}`}
                >
                  <span className="text-slate-500 w-6 text-right">{i + 1}</span>
                  <span className="truncate">{item.label}</span>
                </button>
                <button
                  disabled={!ready}
                  onClick={() => send(CMD.GOTO, { index: i, live: true })}
                  aria-label={`Show ${item.label} live`}
                  className="shrink-0 m-1.5 px-4 rounded-lg bg-red-600 text-sm font-bold disabled:opacity-40 active:scale-95"
                >
                  Live
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default RemoteScreen;
